'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { zhCN } from 'react-day-picker/locale';
import { ChevronDown } from '@/app/components/icons/ChevronDown';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import type { FilterItemConfig } from '@/app/components/shared/FilterBar';
import { FILTER_PRIORITY, FilterButton, FilterDropdown, MoreFiltersDropdown } from '@/app/components/shared/FilterBar';
import { Button } from '@/app/components/ui/button';
import { Calendar } from '@/app/components/ui/calendar';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import { toast } from '@/app/components/ui/use-toast';
import { useIsScreen, useModelVersionText } from '@/app/hooks';
import { cn } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import { FILTER_OPTION_VALUES } from '../_constants';
import type { FileFilters } from '../_hooks/use-file-filters';
import { getModelVersionFilterOptions } from '../_utils';
import type { Query } from '../types';

interface FileFilterBarProps {
  filters: FileFilters;
  onFiltersChange: (updates: Partial<FileFilters>) => void;
  isTeamSpace: boolean;
  creatorFilterOptions: Array<{ key: string; label: string; searchLabel?: string }>;
}

export const FileFilterBar = ({ filters, onFiltersChange, isTeamSpace, creatorFilterOptions }: FileFilterBarProps) => {
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>(undefined);
  const format = useFormatter();
  const t = useTranslations();
  const { isLgScreen, isXlScreen } = useIsScreen();
  const locale = useLocale();
  const { getModelVersionText } = useModelVersionText();

  const filterItemsConfig = useMemo(
    (): FilterItemConfig[] => [
      {
        id: 'fileTypes',
        type: 'dropdown',
        label: t('creations.filters.labels.type'),
        priority: FILTER_PRIORITY.ALWAYS_VISIBLE,
        options: [
          { key: FILTER_OPTION_VALUES.fileTypes.video, label: t('creations.filters.fileTypes.video') },
          { key: FILTER_OPTION_VALUES.fileTypes.image, label: t('creations.filters.fileTypes.image') },
          { key: FILTER_OPTION_VALUES.fileTypes.audio, label: t('creations.filters.fileTypes.audio') },
          { key: FILTER_OPTION_VALUES.fileTypes.folder, label: t('creations.filters.fileTypes.folder') },
        ],
        selectedValues: filters.fileTypes || [],
        onSelectionChange: (fileTypes) => onFiltersChange({ fileTypes: fileTypes as FileFilters['fileTypes'] }),
      },
      {
        id: 'taskTypes',
        type: 'dropdown',
        label: t('creations.filters.labels.taskType'),
        priority: FILTER_PRIORITY.DEFAULT_SCREEN,
        options: [
          {
            key: FILTER_OPTION_VALUES.taskTypes.referenceToVideo,
            label: t('creations.filters.taskTypes.referenceToVideo'),
          },
          { key: FILTER_OPTION_VALUES.taskTypes.imageToVideo, label: t('creations.filters.taskTypes.imageToVideo') },
          { key: FILTER_OPTION_VALUES.taskTypes.textToVideo, label: t('creations.filters.taskTypes.textToVideo') },
          { key: FILTER_OPTION_VALUES.taskTypes.templates, label: t('creations.filters.taskTypes.templates') },
          { key: FILTER_OPTION_VALUES.taskTypes.upscale, label: t('creations.filters.taskTypes.upscale') },
          { key: FILTER_OPTION_VALUES.taskTypes.upscale_image, label: t('creations.filters.taskTypes.upscale_image') },
          {
            key: FILTER_OPTION_VALUES.taskTypes.referenceToImage,
            label: t('creations.filters.taskTypes.referenceToImage'),
          },
          { key: FILTER_OPTION_VALUES.taskTypes.textToImage, label: t('creations.filters.taskTypes.textToImage') },
          { key: FILTER_OPTION_VALUES.taskTypes.auto2video, label: t('creations.filters.taskTypes.auto2video') },
          { key: FILTER_OPTION_VALUES.taskTypes.remix2video, label: t('creations.filters.taskTypes.remix2video') },
          {
            key: FILTER_OPTION_VALUES.taskTypes.extend,
            label: t('creations.filters.taskTypes.extend'),
          },
          { key: FILTER_OPTION_VALUES.taskTypes.videoEdit, label: t('creations.filters.taskTypes.videoEdit') },
          { key: FILTER_OPTION_VALUES.taskTypes.clipCompose, label: t('creations.filters.taskTypes.clipCompose') },
          { key: FILTER_OPTION_VALUES.taskTypes.imageEdit, label: t('creations.filters.taskTypes.imageEdit') },
          { key: FILTER_OPTION_VALUES.taskTypes.faceSwap, label: t('creations.filters.taskTypes.faceSwap') },
          { key: FILTER_OPTION_VALUES.taskTypes.lipSync, label: t('creations.filters.taskTypes.lipSync') },
          { key: FILTER_OPTION_VALUES.taskTypes.oneClick, label: t('creations.filters.taskTypes.oneClick') },
          { key: FILTER_OPTION_VALUES.taskTypes.remake, label: t('creations.filters.taskTypes.remake') },
          { key: FILTER_OPTION_VALUES.taskTypes.musicVideo, label: t('creations.filters.taskTypes.musicVideo') },
          { key: FILTER_OPTION_VALUES.taskTypes.tts, label: t('tools.textToSpeech.title') },
          { key: FILTER_OPTION_VALUES.taskTypes.offlineAvatar, label: t('creations.filters.taskTypes.offlineAvatar') },
          { key: FILTER_OPTION_VALUES.taskTypes.storyGrid, label: t('tools.storyGrid.title') },
          { key: FILTER_OPTION_VALUES.taskTypes.styleTransfer, label: t('creations.filters.taskTypes.styleTransfer') },
          { key: FILTER_OPTION_VALUES.taskTypes.motionControl, label: t('creations.filters.taskTypes.motionControl') },
        ],
        selectedValues: filters.taskTypes || [],
        onSelectionChange: (taskTypes) => onFiltersChange({ taskTypes: taskTypes as FileFilters['taskTypes'] }),
      },
      {
        id: 'resolutions',
        type: 'dropdown',
        label: t('creations.filters.labels.resolution'),
        priority: FILTER_PRIORITY.DEFAULT_SCREEN,
        options: [
          { key: FILTER_OPTION_VALUES.resolutions['4k'], label: t('creations.filters.resolutions.4k') },
          { key: FILTER_OPTION_VALUES.resolutions['2k'], label: t('creations.filters.resolutions.2k') },
          { key: FILTER_OPTION_VALUES.resolutions['1080p'], label: t('creations.filters.resolutions.1080p') },
          { key: FILTER_OPTION_VALUES.resolutions['720p'], label: t('creations.filters.resolutions.720p') },
          { key: FILTER_OPTION_VALUES.resolutions.speed, label: t('creations.filters.resolutions.speed') },
          { key: FILTER_OPTION_VALUES.resolutions.other, label: t('creations.filters.resolutions.other') },
        ],
        selectedValues: filters.resolutions || [],
        onSelectionChange: (resolutions) => onFiltersChange({ resolutions: resolutions as Query['resolution'] }),
      },
      {
        id: 'ratios',
        type: 'dropdown',
        label: t('creations.filters.labels.ratio'),
        priority: FILTER_PRIORITY.DEFAULT_SCREEN,
        options: [
          { key: FILTER_OPTION_VALUES.ratios['16:9'], label: '16 : 9' },
          { key: FILTER_OPTION_VALUES.ratios['9:16'], label: '9 : 16' },
          { key: FILTER_OPTION_VALUES.ratios['1:1'], label: '1 : 1' },
          { key: FILTER_OPTION_VALUES.ratios['4:3'], label: '4 : 3' },
          { key: FILTER_OPTION_VALUES.ratios['3:4'], label: '3 : 4' },
          { key: FILTER_OPTION_VALUES.ratios.other, label: t('creations.filters.ratios.other') },
        ],
        selectedValues: filters.ratios || [],
        onSelectionChange: (ratios) => onFiltersChange({ ratios: ratios as Query['ratio'] }),
      },
      {
        id: 'modelVersions',
        type: 'dropdown',
        label: t('creations.filters.labels.modelVersion'),
        priority: FILTER_PRIORITY.EXTRA_LARGE_ONLY,
        options: getModelVersionFilterOptions({ getModelVersionText }),
        selectedValues: filters.modelVersions || [],
        onSelectionChange: (modelVersions) =>
          onFiltersChange({ modelVersions: modelVersions as Query['model_version'] }),
      },
      ...(isTeamSpace
        ? [
            {
              id: 'creatorIds',
              type: 'dropdown' as const,
              label: t('creations.filters.labels.creator'),
              priority: FILTER_PRIORITY.DEFAULT_SCREEN,
              options: creatorFilterOptions,
              searchable: true,
              searchPlaceholder: t('creations.filters.labels.searchCreator'),
              selectedValues: filters.creatorIds || [],
              onSelectionChange: (creatorIds: string[]) =>
                onFiltersChange({ creatorIds: creatorIds.slice(-1) as FileFilters['creatorIds'] }),
            },
          ]
        : []),
      {
        id: 'reset',
        type: 'button',
        label: t('creations.filters.labels.reset'),
        priority: FILTER_PRIORITY.EXTRA_LARGE_ONLY,
        onClick: () => {
          onFiltersChange({
            dateRange: undefined,
            fileTypes: [],
            taskTypes: [],
            creatorIds: [],
            resolutions: [],
            ratios: [],
            modelVersions: [],
            filterMark: false,
            filterFolder: false,
          });
        },
      },
    ],
    [t, filters, onFiltersChange, isTeamSpace, creatorFilterOptions, locale, getModelVersionText],
  );

  const { directShowItems, moreFilterItems } = useMemo(() => {
    let maxPriorityForDirectShow: number;

    if (isLgScreen) {
      maxPriorityForDirectShow = FILTER_PRIORITY.ALWAYS_VISIBLE;
    } else if (isXlScreen) {
      maxPriorityForDirectShow = FILTER_PRIORITY.EXTRA_LARGE_ONLY;
    } else {
      maxPriorityForDirectShow = FILTER_PRIORITY.DEFAULT_SCREEN;
    }

    const directShowItems = filterItemsConfig.filter((item) => item.priority <= maxPriorityForDirectShow);
    const moreFilterItems = filterItemsConfig.filter((item) => item.priority > maxPriorityForDirectShow);

    return { directShowItems, moreFilterItems };
  }, [isLgScreen, isXlScreen, filterItemsConfig]);

  const formatDateRange = (dateRange: DateRange | undefined): string => {
    if (!dateRange || !dateRange.from) {
      return t('creations.filters.labels.date');
    }

    const formatDate = (date: Date): string => {
      return format.dateTime(date, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    };

    if (!dateRange.to || dateRange.from.getTime() === dateRange.to.getTime()) {
      return formatDate(dateRange.from);
    }

    return `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`;
  };

  const handleDateChange = (newDate: DateRange | undefined) => {
    if (!newDate?.from || !newDate.to) {
      setTempDateRange(newDate);
      return;
    }

    const spanDays = Math.ceil((newDate.to.getTime() - newDate.from.getTime()) / (1000 * 60 * 60 * 24));
    const maxDays = 30;

    if (spanDays > maxDays) {
      const adjustedTo = new Date(newDate.from);
      adjustedTo.setDate(newDate.from.getDate() + maxDays);

      const adjustedDate = {
        from: newDate.from,
        to: adjustedTo,
      };

      setTempDateRange(adjustedDate);
      toast({
        type: 'info',
        message: t('creations.warning.dateRangeLimit', { limit: maxDays }),
      });
    } else {
      setTempDateRange(newDate);
    }
  };

  const handleConfirmDate = () => {
    onFiltersChange({ dateRange: tempDateRange });
    setIsDatePopoverOpen(false);
  };

  const handleCancelDate = () => {
    setTempDateRange(filters.dateRange);
    setIsDatePopoverOpen(false);
  };

  const handleTodayClick = () => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    setTempDateRange({ from: todayStart, to: now });
  };

  const handleClearDateRange = () => {
    setTempDateRange(undefined);
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (open) {
      setTempDateRange(filters.dateRange);
    }
    setIsDatePopoverOpen(open);
  };

  const renderFilterItem = (item: FilterItemConfig) => {
    if (item.type === 'dropdown') {
      return (
        <FilterDropdown
          key={item.id}
          label={item.label || ''}
          options={item.options || []}
          searchable={item.searchable}
          searchPlaceholder={item.searchPlaceholder}
          selectedValues={item.selectedValues || []}
          onSelectionChange={item.onSelectionChange || (() => {})}
        />
      );
    }

    if (item.type === 'checkbox') {
      return (
        <FilterButton
          key={item.id}
          isActive={item.checked || false}
          onClick={() => item.onCheckedChange?.(!item.checked)}
        >
          <CheckboxFill
            checked={item.checked || false}
            onCheckedChange={(checked) => item.onCheckedChange?.(!!checked)}
            className="text-base"
          />
          <span className="text-sm">{item.label}</span>
        </FilterButton>
      );
    }

    if (item.type === 'button') {
      return (
        <FilterButton key={item.id} className="px-2.5" onClick={item.onClick}>
          {item.icon && <item.icon className="text-base" />}
          <span className="text-sm">{item.label}</span>
        </FilterButton>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-1 items-center gap-2 sm:hidden">
      {renderFilterItem({
        id: 'marked',
        type: 'checkbox',
        label: t('creations.filters.labels.mark'),
        priority: FILTER_PRIORITY.ALWAYS_VISIBLE,
        checked: filters.filterMark,
        onCheckedChange: (checked) => onFiltersChange({ filterMark: checked }),
      })}

      <Popover open={isDatePopoverOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <FilterButton
            isActive={!!filters.dateRange}
            className={cn('shrink-0', isDatePopoverOpen ? 'bg-system-white24' : '')}
          >
            <CustomTooltip content={formatDateRange(filters.dateRange)} showOnlyOnOverflow maxLines={1}>
              <span>{formatDateRange(filters.dateRange)}</span>
            </CustomTooltip>
            <ChevronDown className="flex-shrink-0 text-base" />
          </FilterButton>
        </PopoverTrigger>
        <PopoverContent className="w-fit min-w-fit max-w-fit border border-system-white12 bg-[#232529]" align="start">
          <Calendar
            mode="range"
            defaultMonth={tempDateRange?.from || filters.dateRange?.from}
            selected={tempDateRange}
            onSelect={handleDateChange}
            numberOfMonths={2}
            locale={locale === 'zh' ? zhCN : undefined}
            showOutsideDays={false}
            startMonth={new Date('2024-07-01')}
            endMonth={new Date()}
            disabled={{ after: new Date() }}
          />
          <div className="mt-2 flex justify-between border-system-white12 border-t pt-2">
            <div className="flex gap-1">
              <Button variant="text" onClick={handleTodayClick} className="text-system-blue02 hover:text-system-blue01">
                {t('creations.filters.actions.today')}
              </Button>
              <Button
                variant="text"
                onClick={handleClearDateRange}
                className="font-normal text-system-white64 hover:text-system-white80"
              >
                {t('creations.filters.actions.clear')}
              </Button>
            </div>
            <div className="flex gap-1">
              <Button
                variant="text"
                onClick={handleCancelDate}
                className="font-normal text-system-white64 hover:text-system-white80"
              >
                {t('creations.filters.actions.cancel')}
              </Button>
              <Button onClick={handleConfirmDate} className="bg-system-blue02 hover:bg-system-blue01">
                {t('creations.filters.actions.confirm')}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {directShowItems.map(renderFilterItem)}
      {moreFilterItems.length > 0 && <MoreFiltersDropdown items={moreFilterItems} />}
    </div>
  );
};
